import { createSlice } from '@reduxjs/toolkit'

/*
 * services and customizations field are normalized:
 *
 * services format example: {
 *  'id_1': { serviceId: 'id_1', count: 1 },
 *  'id_2': { serviceId: 'id_2', count: 3 },
 * }
 *
 * customizations format example: {
 *  'customization_id1': {
 *    'id_1': { itemId: 'id_1', count: 1 },
 *    'id_2': { itemId: 'id_2', count: 3 },
 *  },
 *  'customization_id2': {
 *    'id_1': { itemId: 'id_1', count: 1 },
 *    'id_2': { itemId: 'id_2', count: 3 },
 *  }
 * }
 */

const initialState = {
  staffNumber: '',
  objectNo: '',
  verificationPhone: '',
  customerContact: {
    name: '',
    phone: '',
    email: '',
  },
  selfBookingReason: '',
  serviceClassificationDetail: {},
  services: {},
  serviceAddress: {
    county: '',
    district: '',
    details: '',
    hasElevator: false,
  },
  moveInAddress: [{ type: 'main' }],
  serviceDateTime: {},
  serviceConfirmTime: {
    selectedDate: null,
    hour: '',
    minute: '',
  },
  isCheckingBookedSession: false,
  contactTime: '不拘(09:00-18:00)',
  payment: {
    method: 'cash',
  },
  coupon: {},
  vatNo: '',
  vatTitle: '',
  customizations: {},
  siteImages: {},
  note: '',
  isSendNewOrderNotify: true,
}

export const selfConstructFormSlice = createSlice({
  name: 'selfConstructForm',
  initialState: initialState,
  reducers: {
    resetForm: (state, action) => {
      return {
        ...initialState,
      }
    },
    setStaffNumber: (state, action) => {
      state.staffNumber = action.payload
    },
    setObjectNo: (state, action) => {
      state.objectNo = action.payload
    },
    setVerificationPhone: (state, action) => {
      state.verificationPhone = action.payload
    },
    setContactName: (state, action) => {
      state.customerContact.name = action.payload
    },
    setContactPhone: (state, action) => {
      state.customerContact.phone = action.payload
    },
    setContactMail: (state, action) => {
      state.customerContact.email = action.payload
    },
    setBookingReason: (state, action) => {
      state.selfBookingReason = action.payload
    },
    // handle service modification
    setServiceClassificationDetail: (state, action) => {
      state.serviceClassificationDetail = action.payload
    },
    resetService: (state, action) => {
      const { services: initService } = initialState
      state.services = initService
    },
    addOneService: (state, action) => {
      const servicePlan = action.payload
      if (state.services[servicePlan?._id]) {
        state.services[servicePlan?._id].count += 1
      } else {
        state.services[servicePlan?._id] = {
          serviceId: servicePlan?._id,
          count: 1,
        }
      }
    },
    removeOneService: (state, action) => {
      const servicePlan = action.payload
      if (state.services[servicePlan?._id]?.count <= 1) {
        delete state.services[servicePlan?._id]
      } else {
        state.services[servicePlan?._id].count -= 1
      }
    },
    toggleService: (state, action) => {
      const {
        servicePlan,
        isServiceSingle,
        inputType,
        crossSelect,
      } = action.payload
      if (state.services[servicePlan?._id]) {
        delete state.services[servicePlan?._id]
      } else {
        if (inputType === 'radio' || isServiceSingle || crossSelect) {
          state.services = {
            [servicePlan?._id]: { serviceId: servicePlan?._id, count: 1 },
          }
        } else {
          state.services[servicePlan?._id] = {
            serviceId: servicePlan?._id,
            count: 1,
          }
        }
      }
    },
    // handle fill address data
    resetServiceAddress: (state, action) => {
      const { serviceAddress: initServiceAddress } = initialState
      state.serviceAddress = initServiceAddress
    },
    setServiceCounty: (state, action) => {
      state.serviceAddress.county = action.payload
    },
    setServiceDistrict: (state, action) => {
      state.serviceAddress.district = action.payload
    },
    setServiceDetails: (state, action) => {
      state.serviceAddress.details = action.payload
    },
    setServiceElevator: (state, action) => {
      state.serviceAddress.hasElevator = action.payload
    },
    // move in addresses control
    addMoveInAddress: (state, action) => {
      state.moveInAddress.push({ type: 'others' })
    },
    removeMoveInAddressByIndex: (state, action) => {
      state.moveInAddress.splice(action.payload, 1)
    },
    updateMoveInAddressByIndex: (state, action) => {
      const { index, address } = action.payload
      state.moveInAddress = state.moveInAddress.map((addressInfo, idx) =>
        idx === index ? address : addressInfo
      )
    },
    replaceMoveInAddress: (state, action) => {
      state.moveInAddress = action.payload
    },
    resetMoveInAddress: (state, action) => {
      const { moveInAddress: initMoveInAddress } = initialState
      state.moveInAddress = initMoveInAddress
    },
    setServiceDateTime: (state, action) => {
      state.serviceDateTime = action.payload
    },
    setServiceConfirmTime: (state, action) => {
      state.serviceConfirmTime = action.payload
    },
    setIsCheckingBookedSession: (state, action) => {
      state.isCheckingBookedSession = action.payload
    },
    resetContactTime: (state, action) => {
      const { contactTime: initContactTime } = initialState
      state.contactTime = initContactTime
    },
    setContactTime: (state, action) => {
      state.contactTime = action.payload
    },
    setPayment: (state, action) => {
      state.payment = action.payload
    },
    setCoupon: (state, action) => {
      state.coupon = action.payload
    },
    setVatNo: (state, action) => {
      state.vatNo = action.payload
    },
    setVatTitle: (state, action) => {
      state.vatTitle = action.payload
    },
    setCustomizations: (state, action) => {
      state.customizations = action.payload
    },
    setSiteImages: (state, action) => {
      state.siteImages = action.payload
    },
    setNote: (state, action) => {
      state.note = action.payload
    },
    setIsSendNewOrderNotify: (state, action) => {
      state.isSendNewOrderNotify = !state.isSendNewOrderNotify
    },
  },
})

export const selectSelfConstructForm = (state) => state.selfConstructForm

export const {
  resetForm,
  setStaffNumber,
  setObjectNo,
  setVerificationPhone,
  setContactName,
  setContactPhone,
  setContactMail,
  setBookingReason,
  setServiceClassificationDetail,
  resetService,
  addOneService,
  removeOneService,
  toggleService,
  resetServiceAddress,
  setServiceCounty,
  setServiceDistrict,
  setServiceDetails,
  setServiceElevator,
  addMoveInAddress,
  removeMoveInAddressByIndex,
  updateMoveInAddressByIndex,
  replaceMoveInAddress,
  resetMoveInAddress,
  setServiceDateTime,
  setServiceConfirmTime,
  setIsCheckingBookedSession,
  resetContactTime,
  setContactTime,
  setPayment,
  setCoupon,
  setVatNo,
  setVatTitle,
  setCustomizations,
  setSiteImages,
  setNote,
  setIsSendNewOrderNotify,
} = selfConstructFormSlice.actions

export default selfConstructFormSlice.reducer
